import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import sidebarLogo from '@/assets/sidebar-logo.png'
import { cn } from '@/lib/utils'
import { useUIStore } from '@/store/uiStore'
import { useAuthStore } from '@/store/authStore'
import { queryClient } from '@/main'
import { LogOut } from 'lucide-react'
import { MAIN_NAV_ROUTES, ADMIN_NAV_ROUTES, SETTINGS_NAV_ROUTE, type NavRoute } from './navRoutes'

export function ExpressiveSidebar() {
  const { t } = useTranslation('common')
  const location = useLocation()
  const { sidebarOpen, setSidebarOpen } = useUIStore()
  const { logout, user } = useAuthStore()

  const handleLogout = () => {
    queryClient.clear()
    logout()
  }

  const initials = user?.username ? user.username.slice(0, 2).toUpperCase() : '??'

  // Cast t to accept any string key — navRoutes uses dynamic keys not in the strict union
  const tAny = t as (k: string) => string

  /** Resolve a route label — literal overrides i18n key. */
  const resolveLabel = (r: NavRoute) => r.labelLiteral ?? tAny(r.labelKey)

  const navItem = (r: NavRoute) => {
    const label = resolveLabel(r)
    const isActive = location.pathname === r.href
    return (
      <Link
        key={r.href}
        to={r.href}
        onClick={() => setSidebarOpen(false)}
        className={cn(
          'mx-3 rounded-xl px-3 py-2 flex items-center gap-3 text-[0.9rem] font-display transition-colors',
          isActive
            ? 'grad-accent text-black font-semibold shadow-elevation-1'
            : 'text-muted-foreground hover:text-foreground hover:bg-accent/60'
        )}
      >
        <r.Icon className="h-[18px] w-[18px] flex-shrink-0" />
        <span className="truncate">{label}</span>
      </Link>
    )
  }

  const sidebarContent = (
    <>
      {/* Header: logo + app name */}
      <Link
        to="/"
        onClick={() => setSidebarOpen(false)}
        className="flex items-center gap-3 px-5 py-5 flex-shrink-0"
      >
        <img
          src={sidebarLogo}
          alt="DeterCamAI"
          className="h-7 w-auto max-w-full object-contain block flex-shrink-0"
        />
        <span className="font-display font-semibold text-foreground text-[0.95rem] truncate">
          DeterCamAI
        </span>
      </Link>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto flex flex-col gap-0.5">
        {/* Main nav */}
        <div className="flex flex-col gap-0.5">
          {MAIN_NAV_ROUTES.map(r => navItem(r))}
        </div>

        {/* Admin section */}
        <div className="flex flex-col gap-0.5">
          <p className="font-display px-6 pt-5 pb-1 text-[11px] uppercase tracking-widest text-muted-foreground/70">
            {t('sidebar.admin')}
          </p>
          {ADMIN_NAV_ROUTES.map(r => user?.is_superuser ? navItem(r) : null)}
          {navItem(SETTINGS_NAV_ROUTE)}
        </div>
      </nav>

      {/* Footer: glass-divider + user initials + name + logout */}
      <div className="border-t border-border/50 p-4 flex items-center gap-3 flex-shrink-0">
        <div className="grad-accent rounded-xl h-8 w-8 flex-shrink-0 flex items-center justify-center text-black text-[11px] font-bold">
          {initials}
        </div>
        {user && (
          <span className="truncate flex-1 text-[0.875rem] text-foreground font-medium min-w-0">
            {user.username}
          </span>
        )}
        <button
          onClick={handleLogout}
          className="flex h-8 w-8 items-center justify-center rounded-xl text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors flex-shrink-0"
          title={t('sidebar.logout')}
        >
          <LogOut className="h-4 w-4" />
        </button>
      </div>
    </>
  )

  return (
    <>
      {/* Desktop: floating glass panel — fixed, inset from edges, rounded */}
      <aside
        data-testid="expressive-sidebar"
        className={cn(
          'fixed left-3 top-3 bottom-3 z-50 w-64 rounded-2xl bg-glass border border-border/50 shadow-elevation-2 flex flex-col overflow-hidden',
          'hidden lg:flex'
        )}
      >
        {sidebarContent}
      </aside>

      {/* Mobile: slide-in floating panel, same glass aesthetic */}
      <aside
        className={cn(
          'fixed left-3 top-3 bottom-3 z-50 w-64 rounded-2xl bg-glass border border-border/50 shadow-elevation-2 flex flex-col overflow-hidden',
          'lg:hidden',
          sidebarOpen ? 'translate-x-0' : '-translate-x-[110%]',
          'transition-transform duration-300'
        )}
      >
        {sidebarContent}
      </aside>
    </>
  )
}
